import React from 'react';
import styled from 'styled-components';
import { useAppState } from '../../../../store/appstate';
import permissionGroups from '../../../../services/helpers/groupPermissions';
import { SessionOrganization } from '../../../../types/SessionUser';
import AddUserToGroup from './AddUserToGroup';
import StyledLink from '../../../atoms/Link/StyledLink';
import { useGroupsStore } from '../../../../store/admin/groups/groupsStore';

const collectedPermissions = permissionGroups.flatMap(x => x.permissions);

const GroupHeader: React.FC = () => {
    const organization = useAppState<SessionOrganization | undefined>(
        s => s.session.selectedOrganization
    );

    const { groupListUsersResponse, isSearching, fetchedUsersInGroup, group } = useGroupsStore();
    const isLoadingOrganization = useAppState<boolean>(s => s.session.isLoadingOrganization);

    const numberOfStores =
        isLoadingOrganization || isSearching
            ? '-'
            : group.connectedStores && group.connectedStores.length > 0
            ? group.connectedStores.length
            : organization?.connectedStores?.length;

    const groupName = group ? group.name : '-';
    const groupPermissions = group ? group.permissions : [];

    const lonePermissions = (permission: string) =>
        !collectedPermissions.includes(permission.split('-')[1]);

    const findPermissions = (category: string[]) => {
        const read = groupPermissions.find(perm => perm === `read-${category[0]}`);
        const write = groupPermissions.find(perm => perm === `write-${category[0]}`);

        return [read, write].filter(x => x);
    };

    const currentPermissionsByCategory = groupPermissions
        ? [
              ...groupPermissions.filter(lonePermissions),
              ...permissionGroups.flatMap(group => findPermissions(group.permissions)),
          ]
        : [];

    return (
        <Row>
            <Col>
                <Label>Manage Organization</Label>
                <UserRow>
                    <StyledLink to="/admin/manage-access#groups">
                        <BoldText>{organization?.name}</BoldText>
                    </StyledLink>
                </UserRow>
            </Col>
            <Col>
                <Label>Group</Label>
                <BoldText>{groupName}</BoldText>
            </Col>
            <Col>
                <Label>Users</Label>
                <UserRow>{fetchedUsersInGroup ? groupListUsersResponse.data.length : '-'}</UserRow>
            </Col>
            <Col>
                <Label>Permissions</Label>
                <div data-testid="number-of-permissions">{currentPermissionsByCategory.length}</div>
            </Col>
            <Col>
                <Label>Stores</Label>
                <>{numberOfStores}</>
            </Col>
            <Actions>
                {organization && (
                    <AddUserToGroup organizationId={organization.id} groupId={group.id} />
                )}
            </Actions>
        </Row>
    );
};

const Row = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    font-size: ${props => props.theme.text.size.medium};
`;

const UserRow = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
    font-size: ${props => props.theme.text.size.medium};
`;
const Col = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 ${props => props.theme.layout.padding.medium};
    border-right: 0.2rem solid ${props => props.theme.colors.text.subtle};
    &:first-child {
        padding-left: 0;
    }
    &:last-child {
        padding-right: 0;
        border-right: 0;
    }
`;
const Label = styled.div`
    font-size: ${props => props.theme.text.size.small};
    margin-bottom: 0.3rem;
    color: ${props => props.theme.colors.text.secondary};
`;

const BoldText = styled.span`
    font-family: ${props => props.theme.text.font.medium};
`;

const Actions = styled.div`
    margin-left: auto;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 1rem;
`;

export default GroupHeader;
